import type {
  NewIngredient,
  NewInstruction,
  NewNutritionFact,
  NewRecipe,
} from '../domain/types.js';
import { ValidationError } from '../domain/errors.js';
import { collapseImageSizeVariants } from './image-variants.js';
import { canonicalizeUnit, parseIngredientLine } from './ingredient.js';
import { resolveImportSourceUrl, type ImportSourceContext } from './source-url.js';

export const MAX_SCHEMA_ORG_IMAGES = 10;

export interface SchemaOrgImportResult {
  recipe: NewRecipe;
  imageUrls: string[];
}

type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

function isRecord(value: unknown): value is Record<string, JsonValue> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function recipeTypeMatches(type: unknown): boolean {
  if (typeof type === 'string') return type === 'Recipe' || type.endsWith('/Recipe');
  if (Array.isArray(type)) return type.some((entry) => recipeTypeMatches(entry));
  return false;
}

function howToSectionMatches(type: unknown): boolean {
  if (typeof type === 'string') return type === 'HowToSection' || type.endsWith('/HowToSection');
  if (Array.isArray(type)) return type.some((entry) => howToSectionMatches(entry));
  return false;
}

function findRecipeNode(doc: unknown): Record<string, JsonValue> | null {
  if (!isRecord(doc)) return null;
  if (recipeTypeMatches(doc['@type'])) return doc;

  const graph = doc['@graph'];
  if (Array.isArray(graph)) {
    for (const entry of graph) {
      if (isRecord(entry) && recipeTypeMatches(entry['@type'])) return entry;
    }
  }
  return null;
}

function parseIsoDurationMinutes(value: unknown): number | null {
  const raw = asString(value);
  if (raw === '') return null;
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i.exec(raw);
  if (match == null) return null;
  const days = Number(match[1] ?? 0);
  const hours = Number(match[2] ?? 0);
  const minutes = Number(match[3] ?? 0);
  const seconds = Number(match[4] ?? 0);
  return Math.round(days * 24 * 60 + hours * 60 + minutes + seconds / 60);
}

function parseYield(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') return asString(value);
  if (Array.isArray(value)) {
    return value
      .map(asString)
      .filter((v) => v !== '')
      .join(', ');
  }
  return '';
}

function parseKeywords(value: unknown): string[] {
  if (typeof value === 'string') {
    return value
      .split(/[,;]/)
      .map((part) => part.trim())
      .filter((part) => part !== '');
  }
  if (Array.isArray(value)) {
    return value.map(asString).filter((part) => part !== '');
  }
  return [];
}

function parseAmountValue(value: unknown): { quantity: string; unit: string; name: string } {
  if (value == null) return { quantity: '', unit: '', name: '' };
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = parseIngredientLine(asString(value));
    return {
      quantity: parsed.quantity ?? '',
      unit: parsed.unit ?? '',
      name: parsed.name,
    };
  }
  if (!isRecord(value)) return { quantity: '', unit: '', name: '' };
  return {
    quantity: asString(value.value) || asString(value.amount),
    unit: canonicalizeUnit(
      asString(value.unitText) || asString(value.unit) || asString(value.unitCode),
    ),
    name: '',
  };
}

function withNote(ingredient: NewIngredient, note: string): NewIngredient {
  return note === '' ? ingredient : { ...ingredient, note };
}

function parseIngredientEntry(entry: unknown): NewIngredient | null {
  if (typeof entry === 'string') {
    const line = entry.trim();
    if (line === '') return null;
    return parseIngredientLine(line);
  }
  if (!isRecord(entry)) return null;

  const named = asString(entry.name) || asString(entry.text);
  const note = asString(entry.description) || asString(entry.note);
  const amount = parseAmountValue(entry.requiredQuantity ?? entry.amount ?? entry.quantity);
  const unit = canonicalizeUnit(asString(entry.unit) || asString(entry.unitText) || amount.unit);

  if (amount.quantity !== '' || unit !== '') {
    const name = named || amount.name;
    if (name === '' && amount.quantity === '') return null;
    if (unit === '' && named !== '') {
      return withNote(parseIngredientLine(`${amount.quantity} ${named}`.trim()), note);
    }
    return withNote(
      {
        name,
        ...(amount.quantity !== '' ? { quantity: amount.quantity } : {}),
        ...(unit !== '' ? { unit } : {}),
      },
      note,
    );
  }

  if (named === '') return null;
  return withNote(parseIngredientLine(named), note);
}

function parseIngredients(value: unknown): NewIngredient[] {
  if (value == null) return [];
  const entries = Array.isArray(value) ? value : [value];
  const out: NewIngredient[] = [];
  for (const entry of entries) {
    const parsed = parseIngredientEntry(entry);
    if (parsed == null) continue;
    if (parsed.name === '' && parsed.quantity == null && parsed.unit == null) continue;
    out.push(parsed);
  }
  return out;
}

function instructionText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (!isRecord(value)) return '';
  return asString(value.text) || asString(value.name) || asString(value.description);
}

function parseInstructions(value: unknown): NewInstruction[] {
  if (value == null) return [];
  const entries = Array.isArray(value) ? value : [value];
  const out: NewInstruction[] = [];

  for (const entry of entries) {
    if (typeof entry === 'string') {
      const text = entry.trim();
      if (text !== '') out.push({ text });
      continue;
    }
    if (!isRecord(entry)) continue;

    if (howToSectionMatches(entry['@type'])) {
      const sectionSteps = entry.itemListElement;
      if (Array.isArray(sectionSteps)) {
        for (const step of sectionSteps) {
          const text = instructionText(step);
          if (text !== '') out.push({ text });
        }
      }
      continue;
    }

    const text = instructionText(entry);
    if (text !== '') out.push({ text });
  }
  return out;
}

function imageUrlFromValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (!isRecord(value)) return null;
  return asString(value.url) || asString(value.contentUrl) || null;
}

function parseImageUrls(value: unknown): string[] {
  if (value == null) return [];
  const entries = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const url = imageUrlFromValue(entry);
    if (url == null) continue;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      continue;
    }
    if (seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return collapseImageSizeVariants(out).slice(0, MAX_SCHEMA_ORG_IMAGES);
}

function parseNutrition(value: unknown): NewNutritionFact[] {
  if (!isRecord(value)) return [];
  const out: NewNutritionFact[] = [];
  for (const [key, raw] of Object.entries(value)) {
    if (key.startsWith('@')) continue;
    const amount = asString(raw);
    if (amount === '') continue;
    out.push({ label: key, amount, unit: '' });
  }
  return out;
}

/**
 * Parse a schema.org Recipe JSON document into a Platewell recipe draft.
 * Image URLs are returned separately for the import queue to download after addRecipe.
 */
export function parseSchemaOrgRecipe(
  json: unknown,
  context: ImportSourceContext = {},
): SchemaOrgImportResult {
  const node = findRecipeNode(json);
  if (node == null) {
    throw new ValidationError('No schema.org Recipe found in this file');
  }

  const title = asString(node.name);
  if (title === '') {
    throw new ValidationError('Recipe is missing a name');
  }

  const fileText =
    context.fileText ??
    (typeof json === 'string' ? json : JSON.stringify(json));
  const source = resolveImportSourceUrl({
    ...context,
    parsedUrl: parseSchemaOrgSourceUrl(node),
    fileText,
  });

  const tags = [
    ...parseKeywords(node.keywords),
    ...parseKeywords(node.recipeCategory),
    ...parseKeywords(node.recipeCuisine),
  ];

  const recipe: NewRecipe = {
    title,
    description: asString(node.description),
    servings: parseYield(node.recipeYield ?? node.yield),
    prepMinutes: parseIsoDurationMinutes(node.prepTime),
    cookMinutes: parseIsoDurationMinutes(node.cookTime),
    ingredients: parseIngredients(node.recipeIngredient),
    instructions: parseInstructions(node.recipeInstructions),
    tags,
    nutritionFacts: parseNutrition(node.nutrition),
    sourceType: source.sourceType,
    sourceUrl: source.sourceUrl,
    sourceText: source.sourceText,
  };

  return {
    recipe,
    imageUrls: parseImageUrls(node.image),
  };
}

function parseSchemaOrgSourceUrl(node: Record<string, JsonValue>): string {
  const direct = asString(node.url);
  if (direct !== '') return direct;

  const page = node.mainEntityOfPage;
  if (typeof page === 'string') return page;
  if (isRecord(page)) {
    const fromPage = asString(page.url) || asString(page['@id']);
    if (fromPage !== '') return fromPage;
  }

  const id = asString(node['@id']);
  if (id.startsWith('http://') || id.startsWith('https://')) return id;

  return '';
}
